import React, { useEffect, useState } from 'react';
import caseService from '../../services/caseService';
import api from '../../services/api';
import { formatDateTime, formatDate } from '../../utils/dateUtils';
import { ClipboardList, Check, RotateCcw, Calendar, User, AlertTriangle, Clock, Plus, Pencil, X, Trash2 } from 'lucide-react';

const CaseTasks = ({ caseId, caseName }) => {
  const [tasks, setTasks] = useState([]);
  const [triageEntries, setTriageEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' or 'triage'
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTriageForm, setShowTriageForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'normal',
    due_date: '',
    sync_to_triage: true,
    sync_to_calendar: false,
  });
  const [editingTask, setEditingTask] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [triageFormData, setTriageFormData] = useState({
    action_description: '',
    assigned_to: '',
    due_date: '',
    notes: '',
    priority: 'normal',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [caseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksData, triageData] = await Promise.all([
        caseService.getTasks(caseId),
        api.get(`/triage/case/${caseId}?include_completed=true`)
      ]);
      setTasks(tasksData);
      setTriageEntries(triageData.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/cases/assignable-users/list');
      setUsers(response.data.all || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await caseService.createTask(caseId, formData);
      setFormData({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'normal',
        due_date: '',
        sync_to_triage: true,
        sync_to_calendar: false,
      });
      setShowAddForm(false);
      loadData();
    } catch (err) {
      alert('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (task) => {
    setEditingTask(task.id);
    setEditFormData({
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      priority: task.priority || 'normal',
      due_date: task.due_date ? task.due_date.slice(0, 16) : '',
    });
  };

  const handleCancelEdit = () => {
    setEditingTask(null);
    setEditFormData({});
  };

  const handleSaveEdit = async (taskId) => {
    try {
      setSubmitting(true);
      await caseService.updateTask(caseId, taskId, editFormData);
      setEditingTask(null);
      setEditFormData({});
      loadData();
    } catch (err) {
      alert('Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/cases/${caseId}/tasks/${taskId}`);
      loadData();
    } catch (err) {
      alert('Failed to delete task');
    }
  };

  const handleTriageSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      // Set due date to close of business (5:00 PM)
      const dueDateWithCOB = new Date(`${triageFormData.due_date}T17:00:00`);

      await api.post('/triage/', {
        ...triageFormData,
        virgo_case_id: caseId,
        case_name: caseName,
        due_date: dueDateWithCOB.toISOString(),
      });

      setTriageFormData({
        action_description: '',
        assigned_to: '',
        due_date: '',
        notes: '',
        priority: 'normal',
      });
      setShowTriageForm(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create triage entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleComplete = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await caseService.updateTask(caseId, task.id, { status: newStatus });
      loadData();
    } catch (err) {
      alert('Failed to update task');
    }
  };

  const handleTriageComplete = async (triageId) => {
    try {
      await api.patch(`/triage/${triageId}/complete`);
      loadData();
    } catch (err) {
      alert('Failed to complete triage entry');
    }
  };

  const handleTriageReopen = async (triageId) => {
    try {
      await api.patch(`/triage/${triageId}/reopen`);
      loadData();
    } catch (err) {
      alert('Failed to reopen triage entry');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
      normal: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
      high: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
      urgent: 'bg-red-500/20 text-red-300 border border-red-500/30',
    };
    return colors[priority] || 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
  };

  const isOverdue = (dueDate, completed) => {
    if (completed) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  const completedTasks = tasks.filter(t => t.status === 'completed');
  const pendingTasks = tasks.filter(t => t.status !== 'completed');
  const pendingTriage = triageEntries.filter(t => t.completed === 0);
  const completedTriage = triageEntries.filter(t => t.completed === 1);

  return (
    <div className="p-6">
      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-virgo-navy-600">
        <button
          onClick={() => setActiveTab('tasks')}
          className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'tasks'
              ? 'border-cyan-500 text-cyan-400'
              : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200'
          }`}
        >
          Case Tasks ({pendingTasks.length})
        </button>
        <button
          onClick={() => setActiveTab('triage')}
          className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors flex items-center ${
            activeTab === 'triage'
              ? 'border-cyan-500 text-cyan-400'
              : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200'
          }`}
        >
          <ClipboardList size={16} className="mr-2" />
          Triage ({pendingTriage.length})
        </button>
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-4">
              <p className="text-sm text-blue-300">Pending Tasks</p>
              <p className="text-3xl font-bold text-white">{pendingTasks.length}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-4">
              <p className="text-sm text-green-300">Completed Tasks</p>
              <p className="text-3xl font-bold text-white">{completedTasks.length}</p>
            </div>
          </div>

          {/* Add Task */}
          <div className="mb-6">
            {!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium shadow-lg shadow-cyan-500/30 transition-all"
              >
                + Add Task
              </button>
            ) : (
              <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Task Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                      placeholder="Enter task title..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                      placeholder="Task details..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Assigned To
                    </label>
                    <select
                      value={formData.assigned_to}
                      onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">-- Unassigned --</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.display_name || user.username}>
                          {user.display_name || user.username} ({user.role})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                        Due Date
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 [color-scheme:dark]"
                      />
                    </div>
                  </div>

                  {/* Smart Sync Options */}
                  <div className="bg-virgo-navy-700/50 border border-virgo-teal-700/30 rounded-lg p-3">
                    <p className="text-xs font-semibold text-virgo-teal-400 mb-2 uppercase tracking-wide">Smart Sync</p>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.sync_to_triage}
                          onChange={(e) => setFormData({ ...formData, sync_to_triage: e.target.checked })}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                        />
                        <span className="text-sm text-virgo-teal-300 group-hover:text-white transition-colors">
                          Add to Triage <span className="text-virgo-teal-500">(high/urgent priority)</span>
                        </span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.sync_to_calendar}
                          onChange={(e) => setFormData({ ...formData, sync_to_calendar: e.target.checked })}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                        />
                        <span className="text-sm text-virgo-teal-300 group-hover:text-white transition-colors">
                          Add deadline to Calendar <span className="text-virgo-teal-500">(requires due date)</span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
                    >
                      {submitting ? 'Creating...' : 'Create Task'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Tasks List */}
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-virgo-teal-400">
              No tasks yet. Create your first task above.
            </div>
          ) : (
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 hover:border-cyan-500/30 transition-all"
                >
                  {editingTask === task.id ? (
                    /* Edit Mode */
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-virgo-teal-400 mb-1">Title</label>
                        <input
                          type="text"
                          value={editFormData.title}
                          onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-virgo-teal-400 mb-1">Description</label>
                        <textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-400 mb-1">Assigned To</label>
                          <select
                            value={editFormData.assigned_to}
                            onChange={(e) => setEditFormData({ ...editFormData, assigned_to: e.target.value })}
                            className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm"
                          >
                            <option value="">-- Unassigned --</option>
                            {users.map((user) => (
                              <option key={user.id} value={user.display_name || user.username}>
                                {user.display_name || user.username}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-400 mb-1">Priority</label>
                          <select
                            value={editFormData.priority}
                            onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                            className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm"
                          >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-400 mb-1">Due Date</label>
                          <input
                            type="datetime-local"
                            value={editFormData.due_date}
                            onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                            className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm [color-scheme:dark]"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1.5 text-sm text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSaveEdit(task.id)}
                          disabled={submitting}
                          className="px-3 py-1.5 text-sm text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                        >
                          {submitting ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={task.status === 'completed'}
                        onChange={() => handleToggleComplete(task)}
                        className="mt-1 h-5 w-5 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-white">{task.title}</h4>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                            <button
                              onClick={() => handleStartEdit(task)}
                              className="p-1 text-virgo-teal-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded transition"
                              title="Edit task"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="p-1 text-virgo-teal-400 hover:text-red-400 hover:bg-red-500/10 rounded transition"
                              title="Delete task"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        {task.description && (
                          <p className="text-sm text-virgo-teal-300 mt-1">{task.description}</p>
                        )}
                        <div className="flex items-center flex-wrap gap-2 mt-2">
                          {task.assigned_to && (
                            <span className="inline-flex items-center text-sm text-cyan-300">
                              <User size={14} className="mr-1" />
                              {task.assigned_to}
                            </span>
                          )}
                          {task.due_date && (
                            <span className="text-sm text-virgo-teal-400">
                              Due: {formatDateTime(task.due_date)}
                            </span>
                          )}
                          {task.triage_action_id && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded">
                              <ClipboardList size={12} className="mr-1" />
                              Triage
                            </span>
                          )}
                          {task.calendar_event_id && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded">
                              <Calendar size={12} className="mr-1" />
                              Calendar
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {completedTasks.length > 0 && (
                <>
                  <h3 className="text-lg font-semibold text-virgo-gold-400 mt-6 mb-3">Completed Tasks</h3>
                  {completedTasks.map((task) => (
                    <div
                      key={task.id}
                      className="bg-virgo-navy-800/30 border border-virgo-navy-600 rounded-xl p-4 opacity-75"
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() => handleToggleComplete(task)}
                          className="mt-1 h-5 w-5 text-green-600 focus:ring-green-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-virgo-teal-400 line-through">{task.title}</h4>
                          {task.description && (
                            <p className="text-sm text-virgo-teal-500 mt-1 line-through">{task.description}</p>
                          )}
                          <p className="text-sm text-green-400 mt-2">
                            Completed: {formatDateTime(task.completed_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Triage Tab */}
      {activeTab === 'triage' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
              <p className="text-sm text-amber-300">Pending Triage</p>
              <p className="text-3xl font-bold text-white">{pendingTriage.length}</p>
            </div>
            <div className="bg-gradient-to-br from-red-500/20 to-rose-500/20 border border-red-500/30 rounded-xl p-4">
              <p className="text-sm text-red-300">Overdue</p>
              <p className="text-3xl font-bold text-white">
                {pendingTriage.filter(t => isOverdue(t.due_date, false)).length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-4">
              <p className="text-sm text-green-300">Completed</p>
              <p className="text-3xl font-bold text-white">{completedTriage.length}</p>
            </div>
          </div>

          {/* Add Triage Entry */}
          <div className="mb-6">
            {!showTriageForm ? (
              <button
                onClick={() => setShowTriageForm(true)}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 font-medium shadow-lg shadow-amber-500/30 transition-all flex items-center"
              >
                <Plus size={18} className="mr-2" />
                Add Triage Entry
              </button>
            ) : (
              <div className="bg-virgo-navy-800/50 border border-amber-500/30 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center">
                  <ClipboardList size={20} className="mr-2" />
                  New Triage Entry
                </h3>
                <form onSubmit={handleTriageSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Action Description *
                    </label>
                    <input
                      type="text"
                      required
                      value={triageFormData.action_description}
                      onChange={(e) => setTriageFormData({ ...triageFormData, action_description: e.target.value })}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-amber-500"
                      placeholder="What needs to be done?"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                        Assigned To *
                      </label>
                      <select
                        required
                        value={triageFormData.assigned_to}
                        onChange={(e) => setTriageFormData({ ...triageFormData, assigned_to: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Select assignee...</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.display_name || user.username}>
                            {user.display_name || user.username} ({user.role})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                        Due Date * <span className="text-virgo-teal-500 text-xs">(COB 5:00 PM)</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={triageFormData.due_date}
                        onChange={(e) => setTriageFormData({ ...triageFormData, due_date: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 [color-scheme:dark]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Priority
                    </label>
                    <select
                      value={triageFormData.priority}
                      onChange={(e) => setTriageFormData({ ...triageFormData, priority: e.target.value })}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={triageFormData.notes}
                      onChange={(e) => setTriageFormData({ ...triageFormData, notes: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-amber-500"
                      placeholder="Additional notes..."
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowTriageForm(false)}
                      className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 transition-all"
                    >
                      {submitting ? 'Creating...' : 'Create Triage Entry'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Triage List */}
          {triageEntries.length === 0 ? (
            <div className="text-center py-12 text-virgo-teal-400">
              No triage entries for this case. Add one above to track actions.
            </div>
          ) : (
            <div className="space-y-3">
              {pendingTriage.map((entry) => (
                <div
                  key={entry.id}
                  className={`bg-virgo-navy-800/50 border rounded-xl p-4 transition-all ${
                    isOverdue(entry.due_date, false)
                      ? 'border-red-500/50 bg-red-900/10'
                      : 'border-virgo-teal-700/30 hover:border-amber-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {isOverdue(entry.due_date, false) ? (
                          <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
                        ) : (
                          <Clock className="text-amber-400 flex-shrink-0" size={20} />
                        )}
                        <h4 className="font-semibold text-white">{entry.action_description}</h4>
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getPriorityColor(entry.priority)}`}>
                          {entry.priority}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-virgo-teal-300 ml-8">
                        <span className="flex items-center">
                          <User size={14} className="mr-1" />
                          {entry.assigned_to}
                        </span>
                        <span className={`flex items-center ${isOverdue(entry.due_date, false) ? 'text-red-400 font-semibold' : ''}`}>
                          <Calendar size={14} className="mr-1" />
                          Due: {formatDate(entry.due_date)}
                        </span>
                      </div>

                      {entry.notes && (
                        <p className="text-sm text-virgo-teal-400 mt-2 ml-8">{entry.notes}</p>
                      )}
                    </div>

                    <button
                      onClick={() => handleTriageComplete(entry.id)}
                      className="p-2 text-green-500 hover:bg-green-500/20 rounded-lg transition"
                      title="Mark Complete"
                    >
                      <Check size={18} />
                    </button>
                  </div>
                </div>
              ))}

              {completedTriage.length > 0 && (
                <>
                  <h3 className="text-lg font-semibold text-virgo-gold-400 mt-6 mb-3">Completed Triage</h3>
                  {completedTriage.map((entry) => (
                    <div
                      key={entry.id}
                      className="bg-virgo-navy-800/30 border border-virgo-navy-600 rounded-xl p-4 opacity-75"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Check className="text-green-500 flex-shrink-0" size={20} />
                            <h4 className="font-semibold text-virgo-teal-400 line-through">{entry.action_description}</h4>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-virgo-teal-500 ml-8">
                            <span className="flex items-center">
                              <User size={14} className="mr-1" />
                              {entry.assigned_to}
                            </span>
                            {entry.completed_date && (
                              <span className="text-green-400">
                                Completed: {formatDate(entry.completed_date)}
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => handleTriageReopen(entry.id)}
                          className="p-2 text-virgo-teal-400 hover:bg-virgo-navy-600 rounded-lg transition"
                          title="Reopen"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CaseTasks;
